// App.js - Composant principal avec routing complet
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform, useMotionValueEvent } from 'framer-motion';
import { useAuth } from './hooks/useAuth';

// Données géographiques
import { WILAYAS_ALPHA, getCommunesByWilaya } from './utils/wilayas';

// Pages principales
import ListeAnnonces from './components/ListeAnnonces';
import DetailAnnonce from './components/DetailAnnonce';
import CreerAnnonce from './components/CreerAnnonce';

// Auth
import LoginPage from './components/auth/LoginPage';
import RegisterPage from './components/auth/RegisterPage';
import PhoneVerification from './components/auth/PhoneVerification';

// Carte
import CartePage from './components/carte/CartePage';

// Outils
import OutilsLanding from './components/tools/OutilsLanding';
import CalculateurHypotheque from './components/tools/CalculateurHypotheque';
import ComparateurQuartiers from './components/tools/ComparateurQuartiers';
import EstimateurValeur from './components/tools/EstimateurValeur';

// Profile
import ProfilePage from './components/profile/ProfilePage';

// Messagerie
import MessageriePage from './components/messagerie/MessageriePage';

// Admin
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminAnnonces from './components/admin/AdminAnnonces';
import AdminUsers from './components/admin/AdminUsers';
import AdminVerifications from './components/admin/AdminVerifications';

// Common
import ProtectedRoute from './components/common/ProtectedRoute';
import AdminRoute from './components/common/AdminRoute';
import ScrollToTop from './components/common/ScrollToTop';
import CurrencyWidget from './components/common/CurrencyWidget';
import { AlertesBadge, AlertesModal } from './components/common/AlertesModal';
import LanguageSwitcher from './i18n/LanguageSwitcher';
import { useI18n } from './i18n/I18nContext';
import { useToast } from './components/common/Toast';
import { useCurrency } from './utils/currency';

import './App.css';
import { API_URL } from './config';

// ─── Normalise le champ photo (string ou JSON array) ────────────────────────
function normaliserAnnonce(a) {
  let photos = [];
  if (a.photo) {
    if (a.photo.startsWith('[')) {
      try { photos = JSON.parse(a.photo); } catch { photos = [a.photo]; }
    } else {
      photos = [a.photo];
    }
  }
  return { ...a, photos, photo: photos[0] || '' };
}

// ─── Arch Icon SVG Component ────────────────────────────────────────────────
function ArchIcon({ size = 52 }) {
  return (
    <svg width={size} height={size} viewBox="-35 -40 70 80">
      <path d="M-28 34 L-28 -8 Q-28 -34 0 -34 Q28 -34 28 -8 L28 34" fill="none" stroke="#85563A" strokeWidth="2.5" strokeLinecap="round"/>
      <path d="M-21 34 L-21 -5 Q-21 -27 0 -27 Q21 -27 21 -5 L21 34" fill="none" stroke="#C08050" strokeWidth="1" opacity="0.45"/>
      <polygon points="0,-16 5,-11 0,-6 -5,-11" fill="#D4856A" opacity="0.7"/>
      <polygon points="-11,2 -7,6 -3,2 -7,-2" fill="#1D9E75" opacity="0.5"/>
      <polygon points="-11,14 -7,18 -3,14 -7,10" fill="#1D9E75" opacity="0.35"/>
      <polygon points="-11,26 -7,30 -3,26 -7,22" fill="#1D9E75" opacity="0.2"/>
      <polygon points="11,2 7,6 3,2 7,-2" fill="#1D9E75" opacity="0.5"/>
      <polygon points="11,14 7,18 3,14 7,10" fill="#1D9E75" opacity="0.35"/>
      <polygon points="11,26 7,30 3,26 7,22" fill="#1D9E75" opacity="0.2"/>
      <polygon points="0,2 4,6 0,10 -4,6" fill="#BA7517" opacity="0.5"/>
      <polygon points="0,14 4,18 0,22 -4,18" fill="#BA7517" opacity="0.35"/>
      <polygon points="0,26 4,30 0,34 -4,30" fill="#BA7517" opacity="0.2"/>
      <polygon points="0,-35 -3,-30 0,-25 3,-30" fill="#85563A"/>
      <line x1="-32" y1="34" x2="32" y2="34" stroke="#85563A" strokeWidth="2" strokeLinecap="round"/>
      <rect x="-32" y="32" width="3" height="4" rx="0.5" fill="#85563A" opacity="0.6"/>
      <rect x="29" y="32" width="3" height="4" rx="0.5" fill="#85563A" opacity="0.6"/>
    </svg>
  );
}

// ─── Hero Section ────────────────────────────────────────────────────────────
function HeroSection({ annoncesCount, onScrollDown }) {
  const heroRef = useRef(null);

  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12 } }
  };

  const fadeUp = {
    hidden:  { opacity: 0, y: 32 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } }
  };

  const fadeIn = {
    hidden:  { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.8, ease: 'easeOut' } }
  };

  const cardVariants = {
    hidden:  { opacity: 0, x: 40, scale: 0.96 },
    visible: { opacity: 1, x: 0, scale: 1, transition: { duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] } }
  };

  return (
    <section className="hero" ref={heroRef}>
      <div className="hero-inner">
        {/* ── LEFT: Text content ── */}
        <motion.div
          className="hero-text"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Eyebrow */}
          <motion.div className="hero-eyebrow" variants={fadeIn}>
            <span className="hero-eyebrow-dot" />
            Immobilier algérien · Diaspora
          </motion.div>

          {/* Title */}
          <motion.h1 className="hero-title" variants={fadeUp}>
            Trouvez votre bien
            <span className="hero-title-line2"> en Algérie.</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p className="hero-subtitle" variants={fadeUp}>
            La première plateforme immobilière pensée pour la diaspora algérienne.
            Annonces vérifiées, prix transparents, guide administratif inclus.
          </motion.p>

          {/* Stats */}
          <motion.div className="hero-stats" variants={fadeUp}>
            <div className="hero-stat">
              <span className="hero-stat-num">{annoncesCount || 10}+</span>
              <span className="hero-stat-label">Annonces</span>
            </div>
            <div className="hero-stat">
              <span className="hero-stat-num">100%</span>
              <span className="hero-stat-label">Vérifiées</span>
            </div>
            <div className="hero-stat">
              <span className="hero-stat-num">€ ↔ DA</span>
              <span className="hero-stat-label">Convertisseur</span>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div className="hero-cta" variants={fadeUp}>
            <button className="hero-btn-primary" onClick={onScrollDown}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              Voir les annonces
            </button>
            <button className="hero-btn-ghost" onClick={onScrollDown}>
              Comment ça marche
            </button>
          </motion.div>
        </motion.div>

        {/* ── RIGHT: Floating card preview ── */}
        <motion.div
          className="hero-card-preview"
          variants={cardVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="hero-card-preview-img">
            <img
              src="https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=400&h=200&fit=crop"
              alt="Bien immobilier Alger"
            />
            <span className="hero-preview-badge">Vente</span>
          </div>
          <div className="hero-preview-title">Villa F5 — Hydra, Alger</div>
          <div className="hero-preview-price">35 000 000 DA</div>
          <div className="hero-preview-location">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
            </svg>
            Hydra · 240 m²
          </div>
          <div className="hero-preview-verified">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            Annonce vérifiée
          </div>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <div className="hero-scroll-cue">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
        Défiler
      </div>
    </section>
  );
}

// ─── Navbar unifiée (brand + liens + auth + outils) ────────────────────────
function AppNavbar({ annoncesCount }) {
  const { user, isAuthenticated, logout, accessToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const addToast = useToast();
  const [alertesOpen, setAlertesOpen] = React.useState(false);
  const [adminPending, setAdminPending] = React.useState(0);
  const [scrolled, setScrolled] = React.useState(false);
  const { t } = useI18n();

  // Scroll effect for navbar
  React.useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  React.useEffect(() => {
    if (!isAuthenticated || user?.role !== 'admin' || !accessToken) return;
    fetch(`${API_URL}/admin/stats`, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(r => r.json())
      .then(d => {
        const total = (d.annonces?.enAttente || 0) + (d.verifications?.enAttente || 0);
        setAdminPending(total);
      })
      .catch(() => {});
  }, [isAuthenticated, user, accessToken]);

  const handleLogout = async () => {
    await logout();
    addToast('Vous avez été déconnecté.', 'info');
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const initiales = user
    ? `${(user.prenom || '')[0] || ''}${(user.nom || '')[0] || ''}`.toUpperCase() || 'U'
    : '';

  return (
    <motion.nav
      className={`app-navbar${scrolled ? ' navbar-scrolled' : ''}`}
      initial={{ y: -64, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="navbar-inner">
        {/* Col 1 : Brand */}
        <div className="navbar-brand" onClick={() => navigate('/')} role="button" tabIndex={0}>
          <ArchIcon size={36} />
          <div className="navbar-brand-text">
            <span className="navbar-brand-name">DARNA IMMO</span>
            <span className="navbar-brand-tagline">IMMOBILIER &amp; ART DE VIVRE</span>
          </div>
          {annoncesCount > 0 && (
            <span className="navbar-count">{annoncesCount}</span>
          )}
        </div>

        {/* Col 2 : Liens navigation */}
        <div className="navbar-links">
          <button
            className={`nav-link ${isActive('/') && !isActive('/carte') && !isActive('/outils') && !isActive('/profile') && !isActive('/login') && !isActive('/register') ? 'active' : ''}`}
            onClick={() => navigate('/')}
          >
            {t.nav.home}
          </button>
          <button
            className={`nav-link ${isActive('/carte') ? 'active' : ''}`}
            onClick={() => navigate('/carte')}
          >
            {t.nav.map}
          </button>
          <button
            className={`nav-link ${isActive('/outils') ? 'active' : ''}`}
            onClick={() => navigate('/outils')}
          >
            {t.nav.tools}
          </button>
          {isAuthenticated && (
            <button
              className={`nav-link ${isActive('/messages') ? 'active' : ''}`}
              onClick={() => navigate('/messages')}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
              </svg>
              {t.nav.messages}
            </button>
          )}
          {isAuthenticated && (
            <button
              className="nav-link nav-link-publish"
              onClick={() => navigate('/creer')}
            >
              {t.nav.publish}
            </button>
          )}
          {isAuthenticated && user?.role === 'admin' && (
            <button
              className={`nav-link nav-link-admin ${isActive('/admin') ? 'active' : ''}`}
              onClick={() => navigate('/admin')}
            >
              Admin
              {adminPending > 0 && (
                <span className="nav-admin-badge">
                  {adminPending > 99 ? '99+' : adminPending}
                </span>
              )}
            </button>
          )}
        </div>

        {/* Col 3 : Auth */}
        <div className="navbar-auth">
          {isAuthenticated ? (
            <>
              <button className="navbar-auth-user" onClick={() => navigate('/profile')}>
                <div className="nav-avatar">{initiales}</div>
                <span className="navbar-auth-name">{user?.prenom}</span>
              </button>
              <button className="navbar-auth-logout" onClick={handleLogout}>
                {t.nav.logout}
              </button>
            </>
          ) : (
            <>
              <button className="navbar-auth-login" onClick={() => navigate('/login')}>
                {t.nav.login}
              </button>
              <button className="navbar-auth-register" onClick={() => navigate('/register')}>
                {t.nav.register}
              </button>
            </>
          )}
        </div>

        {/* Col 4 : Outils */}
        <div className="navbar-tools">
          <LanguageSwitcher />
          <AlertesBadge onClick={() => setAlertesOpen(true)} />
          <CurrencyWidget />
        </div>
      </div>
      <AlertesModal open={alertesOpen} onClose={() => setAlertesOpen(false)} />
    </motion.nav>
  );
}

// ─── Detail annonce route wrapper ──────────────────────────────────────────
function DetailAnnonceRoute({ annonces, onSupprimer }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const annonce = annonces.find(a => String(a.id) === String(id));

  if (!annonce) {
    return (
      <div className="container" style={{ padding: '40px', textAlign: 'center' }}>
        <p>Annonce introuvable.</p>
        <button className="btn btn-ghost" onClick={() => navigate('/')}>Retour aux annonces</button>
      </div>
    );
  }

  // Annonces similaires : même type ou même quartier, excluant l'annonce courante
  const similaires = annonces
    .filter(a => String(a.id) !== String(id) && (a.type === annonce.type || a.quartier === annonce.quartier))
    .slice(0, 3);

  return (
    <div className="container">
      <DetailAnnonce
        annonce={annonce}
        similaires={similaires}
        onRetour={() => navigate('/')}
        onSupprimer={() => { onSupprimer(annonce.id); navigate('/'); }}
        onVoirDetail={(aid) => navigate(`/annonces/${aid}`)}
      />
    </div>
  );
}

// ─── App principale ────────────────────────────────────────────────────────
function App() {
  const [annonces, setAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtres, setFiltres] = useState({
    wilaya: '', commune: '', typeLogement: '',
    prixMin: '', prixMax: '',
    transaction: '',           // '' | 'vente' | 'location'
    surfaceMin: '', surfaceMax: '',
    chambres: '',              // '' | '1' | '2' | '3' | '4' | '5+'
    etat: '',                  // '' | 'neuf' | 'bon_etat' | 'a_renover'
    equipements: [],           // ['parking','balcon','ascenseur','meuble','gardien']
  });
  const [filtreDevise, setFiltreDevise] = useState('DZD'); // 'DZD' | 'EUR_OFFICIEL' | 'EUR_PARALLELE'
  const [filtresAvancesOpen, setFiltresAvancesOpen] = useState(false);
  const { rates } = useCurrency();

  // Communes disponibles selon la wilaya sélectionnée
  const communesDisponibles = useMemo(
    () => filtres.wilaya ? getCommunesByWilaya(filtres.wilaya) : [],
    [filtres.wilaya]
  );
  const [favoris, setFavoris] = useState([]);
  const { isLoading: authLoading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    chargerAnnonces();
    try {
      const saved = JSON.parse(localStorage.getItem('darna-immo_favoris') || '[]');
      setFavoris(saved);
    } catch (e) { /* ignore */ }
  }, []);

  const chargerAnnonces = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/annonces?limit=50`);
      if (!res.ok) throw new Error('Erreur chargement');
      const data = await res.json();
      // L'API retourne { annonces: [...], pagination: {...} }
      const liste = Array.isArray(data) ? data : (data.annonces || []);
      setAnnonces(liste.map(normaliserAnnonce));
    } catch (err) {
      setError('Impossible de charger les annonces. Vérifiez que le serveur est lancé.');
    } finally {
      setLoading(false);
    }
  };

  const appliquerFiltres = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (filtres.wilaya)       params.append('wilaya',       filtres.wilaya);
      if (filtres.commune)      params.append('commune',      filtres.commune);
      if (filtres.typeLogement) params.append('typeLogement', filtres.typeLogement);
      if (filtres.transaction)  params.append('transaction',  filtres.transaction);
      if (filtres.surfaceMin)   params.append('surfaceMin',   filtres.surfaceMin);
      if (filtres.surfaceMax)   params.append('surfaceMax',   filtres.surfaceMax);
      if (filtres.chambres) {
        params.append('chambresMin', filtres.chambres === '5+' ? '5' : filtres.chambres);
      }
      if (filtres.etat)         params.append('etat',         filtres.etat);
      filtres.equipements.forEach(eq => params.append('equipements', eq));

      const toDZD = (val) => {
        const n = Number(val);
        if (!val || isNaN(n)) return '';
        if (filtreDevise === 'EUR_OFFICIEL')  return Math.round(n * rates.officiel);
        if (filtreDevise === 'EUR_PARALLELE') return Math.round(n * rates.parallele);
        return n;
      };
      if (filtres.prixMin) params.append('prixMin', toDZD(filtres.prixMin));
      if (filtres.prixMax) params.append('prixMax', toDZD(filtres.prixMax));

      const res = await fetch(`${API_URL}/annonces/search/filter?${params.toString()}`);
      const data = await res.json();
      const liste = Array.isArray(data) ? data : (data.annonces || []);
      setAnnonces(liste.map(normaliserAnnonce));
    } catch (err) {
      setError('Erreur lors du filtrage');
    } finally {
      setLoading(false);
    }
  };

  const toggleFavori = (id) => {
    setFavoris(prev => {
      const next = prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id];
      try { localStorage.setItem('darna-immo_favoris', JSON.stringify(next)); } catch(e) {}
      return next;
    });
  };

  const creerAnnonce = async (nouvelleAnnonce) => {
    try {
      const res = await fetch(`${API_URL}/annonces`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(nouvelleAnnonce),
      });
      if (!res.ok) throw new Error('Erreur création');
      await chargerAnnonces();
    } catch (err) {
      alert("Erreur lors de la création de l'annonce");
    }
  };

  const supprimerAnnonce = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette annonce ?')) return;
    try {
      const res = await fetch(`${API_URL}/annonces/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Erreur suppression');
      await chargerAnnonces();
    } catch (err) {
      alert('Erreur lors de la suppression');
    }
  };

  const navigate = useNavigate();
  const { t } = useI18n();
  const annoncesRef = useRef(null);

  const scrollToAnnonces = () => {
    annoncesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ── Scroll reveal : IntersectionObserver ──────────────────────────────────
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    const observe = () => {
      document.querySelectorAll('.reveal, .reveal-left, .reveal-scale').forEach((el) => {
        if (el.classList.contains('is-visible')) return;
        const rect = el.getBoundingClientRect();
        // Above-fold elements: reveal instantly with no transition
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          el.style.transitionDuration = '0s';
          el.classList.add('is-visible');
          requestAnimationFrame(() => { el.style.transitionDuration = ''; });
        } else {
          observer.observe(el);
        }
      });
    };
    observe();
    const timer = setTimeout(observe, 150);
    return () => { observer.disconnect(); clearTimeout(timer); };
  }, [location.pathname, annonces.length]);

  // Masquer header/navbar sur les pages admin et auth
  const isAuthPage = ['/login', '/register', '/verification'].includes(location.pathname);
  const isAdminPage = location.pathname.startsWith('/admin');
  const showLayout = !isAuthPage && !isAdminPage;

  return (
    <div className="App">
      <ScrollToTop />

      {showLayout && <AppNavbar annoncesCount={annonces.length} />}

      <main className={showLayout ? 'app-main' : 'app-main-full'}>
        <AnimatePresence mode="wait">
        <Routes>
          {/* ── Public ── */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verification" element={<PhoneVerification />} />

          {/* ── Carte ── */}
          <Route path="/carte" element={<CartePage />} />

          {/* ── Outils ── */}
          <Route path="/outils" element={<OutilsLanding />} />
          <Route path="/outils/calculateur" element={<CalculateurHypotheque />} />
          <Route path="/outils/comparateur" element={<ComparateurQuartiers />} />
          <Route path="/outils/estimateur" element={<EstimateurValeur />} />

          {/* ── Profile (protégé) ── */}
          <Route path="/profile" element={<ProtectedRoute><div className="container"><ProfilePage /></div></ProtectedRoute>} />

          {/* ── Messagerie (protégé) ── */}
          <Route path="/messages" element={<ProtectedRoute><div className="container"><MessageriePage /></div></ProtectedRoute>} />

          {/* ── Admin (protégé admin) ── */}
          <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="annonces" element={<AdminAnnonces />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="verifications" element={<AdminVerifications />} />
          </Route>

          {/* ── Créer annonce (protégé) ── */}
          <Route
            path="/creer"
            element={
              <ProtectedRoute>
                <div className="container">
                  <CreerAnnonce
                    onCreate={async (data) => { await creerAnnonce(data); navigate('/'); }}
                    onRetour={() => navigate('/')}
                  />
                </div>
              </ProtectedRoute>
            }
          />

          {/* ── Détail annonce ── */}
          <Route
            path="/annonces/:id"
            element={<DetailAnnonceRoute annonces={annonces} onSupprimer={supprimerAnnonce} />}
          />

          {/* ── Liste annonces (accueil) ── */}
          <Route
            path="/"
            element={
              <motion.div
                key="home"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {/* Hero */}
                <HeroSection annoncesCount={annonces.length} onScrollDown={scrollToAnnonces} />

                <div ref={annoncesRef} className="container">
                {/* Filtres */}
                <div className="filters">
                  <div className="filters-header">
                    <h2>{t.search.title}</h2>
                    <span className="filters-subtitle">{t.search.subtitle}</span>
                  </div>

                  {/* ── Toggle Transaction (Vente / Location) ── */}
                  <div className="filter-transaction-toggle">
                    {[
                      { key: '', label: 'Tout',     icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/></svg> },
                      { key: 'vente',    label: 'Vente',    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> },
                      { key: 'location', label: 'Location', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg> },
                    ].map(({ key, label, icon }) => (
                      <button
                        key={key}
                        type="button"
                        className={`filter-transaction-btn${filtres.transaction === key ? ' active' : ''}`}
                        onClick={() => setFiltres(f => ({ ...f, transaction: key }))}
                      >
                        {icon}
                        {label}
                      </button>
                    ))}
                  </div>

                  <div className="filter-grid">

                    {/* ── Wilaya ── */}
                    <div className="filter-item">
                      <label>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                        {t.search.wilaya}
                      </label>
                      <div className="filter-select-wrap">
                        <select
                          value={filtres.wilaya}
                          onChange={e => setFiltres({ ...filtres, wilaya: e.target.value, commune: '' })}
                        >
                          <option value="">— {t.search.wilaya} —</option>
                          {WILAYAS_ALPHA.map(w => (
                            <option key={w.code} value={w.nom}>{w.nom} ({w.code})</option>
                          ))}
                        </select>
                        <svg className="filter-select-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                      </div>
                    </div>

                    {/* ── Commune (cascading) ── */}
                    <div className="filter-item">
                      <label>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-4 0v2"/><path d="M8 7V5a2 2 0 00-4 0v2"/></svg>
                        {t.search.commune}
                      </label>
                      <div className={`filter-select-wrap ${!filtres.wilaya ? 'filter-select-disabled' : ''}`}>
                        <select
                          value={filtres.commune}
                          onChange={e => setFiltres({ ...filtres, commune: e.target.value })}
                          disabled={!filtres.wilaya}
                        >
                          <option value="">{filtres.wilaya ? `— ${t.search.commune} —` : `— ${t.search.wilaya} —`}</option>
                          {communesDisponibles.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <svg className="filter-select-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                      </div>
                    </div>

                    {/* ── Type de logement ── */}
                    <div className="filter-item">
                      <label>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                        {t.search.type}
                      </label>
                      <div className="filter-select-wrap">
                        <select
                          value={filtres.typeLogement}
                          onChange={e => setFiltres({ ...filtres, typeLogement: e.target.value })}
                        >
                          <option value="">— {t.search.type} —</option>
                          <option value="Appartement">🏢 {t.annonce.types.appartement}</option>
                          <option value="Villa">🏡 {t.annonce.types.villa}</option>
                          <option value="Maison">🏠 Maison</option>
                          <option value="Duplex">🏘 Duplex</option>
                          <option value="Studio">🛋 {t.annonce.types.studio}</option>
                          <option value="Terrain">🌿 {t.annonce.types.terrain}</option>
                          <option value="Local">🏪 {t.annonce.types.local}</option>
                        </select>
                        <svg className="filter-select-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                      </div>
                    </div>

                    {/* ── Devise + Prix ── */}
                    <div className="filter-item filter-item-full">
                      <label>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
                        Prix
                      </label>
                      <div className="filter-devise-row">
                        <div className="filter-devise-toggle">
                          {[
                            { key: 'DZD',           label: 'DZD',        sub: 'Dinar' },
                            { key: 'EUR_OFFICIEL',  label: '€ Officiel', sub: `${Math.round(rates.officiel)} DZD` },
                            { key: 'EUR_PARALLELE', label: '€ Square',   sub: `${Math.round(rates.parallele)} DZD` },
                          ].map(d => (
                            <button key={d.key} type="button"
                              className={`filter-devise-btn${filtreDevise === d.key ? ' active' : ''}`}
                              onClick={() => { setFiltreDevise(d.key); setFiltres(f => ({ ...f, prixMin: '', prixMax: '' })); }}
                            >
                              <span className="filter-devise-label">{d.label}</span>
                              <span className="filter-devise-sub">{d.sub}</span>
                            </button>
                          ))}
                        </div>
                        <div className="filter-prix-row">
                          <div className="filter-prix-input-wrap">
                            <input type="number" placeholder="0"
                              value={filtres.prixMin}
                              onChange={e => setFiltres({ ...filtres, prixMin: e.target.value })}
                            />
                            <span className="filter-prix-suffix">{filtreDevise === 'DZD' ? 'DZD' : '€'} min</span>
                          </div>
                          <div className="filter-prix-input-wrap">
                            <input type="number" placeholder={filtreDevise === 'DZD' ? '99 999 999' : '500 000'}
                              value={filtres.prixMax}
                              onChange={e => setFiltres({ ...filtres, prixMax: e.target.value })}
                            />
                            <span className="filter-prix-suffix">{filtreDevise === 'DZD' ? 'DZD' : '€'} max</span>
                          </div>
                        </div>
                      </div>
                    </div>

                  </div>

                  {/* ── Filtres avancés (collapsible) ── */}
                  <button
                    type="button"
                    className={`filtres-avances-toggle${filtresAvancesOpen ? ' open' : ''}`}
                    onClick={() => setFiltresAvancesOpen(o => !o)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/>
                    </svg>
                    Filtres avancés
                    {(filtres.surfaceMin || filtres.surfaceMax || filtres.chambres || filtres.etat || filtres.equipements.length > 0) && (
                      <span className="filtres-avances-badge">
                        {[filtres.surfaceMin, filtres.surfaceMax, filtres.chambres, filtres.etat].filter(Boolean).length + filtres.equipements.length}
                      </span>
                    )}
                    <svg className="filtres-avances-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>

                  {filtresAvancesOpen && (
                    <div className="filtres-avances-panel">

                      {/* Surface */}
                      <div className="fa-section">
                        <label className="fa-label">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/></svg>
                          Surface
                        </label>
                        <div className="fa-row">
                          <div className="filter-prix-input-wrap">
                            <input type="number" placeholder="0"
                              value={filtres.surfaceMin}
                              onChange={e => setFiltres(f => ({ ...f, surfaceMin: e.target.value }))}
                            />
                            <span className="filter-prix-suffix">m² min</span>
                          </div>
                          <div className="filter-prix-input-wrap">
                            <input type="number" placeholder="500"
                              value={filtres.surfaceMax}
                              onChange={e => setFiltres(f => ({ ...f, surfaceMax: e.target.value }))}
                            />
                            <span className="filter-prix-suffix">m² max</span>
                          </div>
                        </div>
                      </div>

                      {/* Chambres */}
                      <div className="fa-section">
                        <label className="fa-label">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 7v11a2 2 0 002 2h14a2 2 0 002-2V7"/><path d="M21 7H3l2-4h14l2 4z"/></svg>
                          Chambres
                        </label>
                        <div className="fa-chips">
                          {['', '1', '2', '3', '4', '5+'].map(c => (
                            <button key={c} type="button"
                              className={`fa-chip${filtres.chambres === c ? ' active' : ''}`}
                              onClick={() => setFiltres(f => ({ ...f, chambres: c }))}
                            >
                              {c === '' ? 'Tout' : c === '5+' ? '5+ 🏠' : `${c} ch.`}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* État */}
                      <div className="fa-section">
                        <label className="fa-label">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                          État du bien
                        </label>
                        <div className="fa-chips">
                          {[
                            { key: '', label: 'Tous' },
                            { key: 'neuf', label: '✨ Neuf' },
                            { key: 'bon_etat', label: '👍 Bon état' },
                            { key: 'a_renover', label: '🔨 À rénover' },
                          ].map(({ key, label }) => (
                            <button key={key} type="button"
                              className={`fa-chip${filtres.etat === key ? ' active' : ''}`}
                              onClick={() => setFiltres(f => ({ ...f, etat: key }))}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Équipements */}
                      <div className="fa-section">
                        <label className="fa-label">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 8 12 12 14 14"/></svg>
                          Équipements
                        </label>
                        <div className="fa-chips fa-chips-eq">
                          {[
                            { key: 'parking',   label: '🚗 Parking' },
                            { key: 'balcon',    label: '🌿 Balcon' },
                            { key: 'ascenseur', label: '🛗 Ascenseur' },
                            { key: 'meuble',    label: '🛋 Meublé' },
                            { key: 'gardien',   label: '🔒 Gardien' },
                          ].map(({ key, label }) => {
                            const checked = filtres.equipements.includes(key);
                            return (
                              <button key={key} type="button"
                                className={`fa-chip fa-chip-eq${checked ? ' active' : ''}`}
                                onClick={() => setFiltres(f => ({
                                  ...f,
                                  equipements: checked
                                    ? f.equipements.filter(e => e !== key)
                                    : [...f.equipements, key]
                                }))}
                              >
                                {checked && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                    </div>
                  )}

                  <div className="filters-actions">
                    <button className="btn btn-primary btn-search" onClick={appliquerFiltres}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                      </svg>
                      {t.search.btn}
                    </button>
                    <button className="btn btn-ghost" onClick={() => {
                      setFiltres({ wilaya: '', commune: '', typeLogement: '', prixMin: '', prixMax: '', transaction: '', surfaceMin: '', surfaceMax: '', chambres: '', etat: '', equipements: [] });
                      setFiltreDevise('DZD');
                      setFiltresAvancesOpen(false);
                      chargerAnnonces();
                    }}>
                      {t.search.reset}
                    </button>
                  </div>
                </div>

                {/* États */}
                {error && (
                  <div className="error-banner">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                    </svg>
                    <p>{error}</p>
                  </div>
                )}
                {!loading && annonces.length === 0 && !error && (
                  <div className="empty-state">
                    <div className="empty-state-icon">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                        <polyline points="9 22 9 12 15 12 15 22"/>
                      </svg>
                    </div>
                    <h3>Aucune annonce trouvée</h3>
                    <p>Essayez de modifier vos critères de recherche</p>
                  </div>
                )}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: loading ? 0 : 1 }}
                  transition={{ duration: 0.4 }}
                  style={{ minHeight: annonces.length > 0 ? 'auto' : 0 }}
                >
                  {annonces.length > 0 && (
                    <ListeAnnonces
                      annonces={annonces}
                      onVoirDetail={(id) => navigate(`/annonces/${id}`)}
                      favoris={favoris}
                      onToggleFavori={toggleFavori}
                    />
                  )}
                </motion.div>
                </div>
              </motion.div>
            }
          />
        </Routes>
        </AnimatePresence>
      </main>

      {/* Footer */}
      {showLayout && (
        <footer className="footer">
          <div className="footer-inner">
            <div className="footer-brand">
              <ArchIcon size={44} />
              <span className="footer-name">Darna Immo</span>
              <p className="footer-desc">Votre partenaire immobilier de confiance à Alger depuis 2025.</p>
            </div>
            <div className="footer-links">
              <div className="footer-col">
                <h4>Navigation</h4>
                <button onClick={() => navigate('/')}>Toutes les annonces</button>
                <button onClick={() => navigate('/carte')}>Carte interactive</button>
                <button onClick={() => navigate('/outils')}>Outils</button>
                <button onClick={() => navigate('/messages')}>Messagerie</button>
              </div>
              <div className="footer-col">
                <h4>Contact</h4>
                <span>support@darna-immo.dz</span>
                <span>+213 555 123 456</span>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2026 Darna Immo - Tous droits réservés</p>
          </div>
        </footer>
      )}
    </div>
  );
}

export default App;
